import { SwipeRightAlt } from '@mui/icons-material';
import CancelIcon from '@mui/icons-material/Cancel';
import SaveIcon from '@mui/icons-material/Save';
import { Button, Dialog, DialogActions, DialogTitle, Tooltip } from '@mui/material';
import { action, computed, makeObservable, observable, reaction, runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';
import Configuracao from '../../util/Configuracao';
import ErrorUtil from '../../util/ErrorUtil';
import NumeroProcesso from '../NumeroProcesso';
import PainelFiltros from '../PainelFiltros';
import ReactComponent from '../ReactComponent';
import CodigoDecisao from './CodigoDecisao';
import FiltroRapido from './FiltroRapido';
import MovimentoCNJ from './MovimentoCNJ';
import OperacoesEmLoteSelecionar from './OperacoesEmLoteSelecionar';
import PainelProcessosOperacoesEmLote from './PainelProcessosOperacoesEmLote';

class MovimentoDecisaoLote extends ReactComponent {
    dialogOpen = false;
    movimentosSelecionados = [];
    idDecisaoSelecionada;

    constructor(props) {
        super(props);

        makeObservable(this, {
            dialogOpen: observable,
            movimentosSelecionados: observable,
            idDecisaoSelecionada: observable,
            handleClickIcone: action,
            handleCloseDialog: action,
            processos: computed,
            processosSelecionados: computed,
            existeProcessoSIJSelecionado: computed,
            existeProcessoPJeSelecionado: computed,
            limparProcessoPesquisa: action,
            mensagemLoteSemProcessos: computed,
            handleClickBotaoRegistrar: action,
            gravarDecisao: action,
            gravarMovimentos: action,
            callbackDecisaoSelecionada: action,
            callbackMovimentosSelecionados: action,
            renderConteudoDialog: computed,
            botaoRegistrarDesabilitado: computed,
            tooltipBotaoRegistrar: computed
        });

        this.createReaction(
            reaction(
                () => this.dialogOpen,
                () => this.limparProcessoPesquisa()
            )
        );
    }

    handleClickIcone = () => {
        this.movimentosSelecionados = [];
        this.idDecisaoSelecionada = undefined;
        this.dialogOpen = true;
    };

    handleCloseDialog = () => {
        this.dialogOpen = false;
    };

    get processos() {
        return (this.store.processosFiltrados || []).filter((registro) => this.filtrarProcessoPeloNumero(registro));
    }

    get processosSelecionados() {
        return (this.processosSelecionaveis || []).filter((registro) => this.store.isCheckedOperacoesEmLote(registro));
    }

    get processosSelecionaveis() {
        return (this.processos || []).filter(
            (registro) => !registro.necessitaImportacaoSalaSessao && !registro.movimentosTransferidos
        );
    }

    get existeProcessoSIJSelecionado() {
        return this.processosSelecionados.find((registro) => registro.processo.origem === Configuracao.ORIGEM_SIJ);
    }

    get existeProcessoPJeSelecionado() {
        return this.processosSelecionados.find((registro) => registro.processo.origem === Configuracao.ORIGEM_PJE);
    }

    limparProcessoPesquisa = () => {
        this.processoPesquisa = { numero: '', digito: '', ano: '', orgao: '', regional: '', vara: '' };
    };

    get mensagemLoteSemProcessos() {
        return 'Não há processos na sessão' + this.complementoMensagemFiltrando;
    }

    handleClickBotaoRegistrar = async () => {
        const processosNotificar = [...this.processosSelecionados];
        const movimentos = this.getMovimentosSelecionadosParaGravar();
        const decisao = this.getDecisaoSelecionadaParaGravar();

        await this.store.processarLote({
            titulo: 'Registrando ' + this.label,
            qtdTotalProcessos: this.processosSelecionados.length,
            lotesProcessos: this.processosSelecionados,
            funcaoOperacao: (registro) => this.gravarMovimentosOuDecisao(registro, movimentos, decisao)
        });
        this.salaSessaoStore.notificarJulgamentoLote(processosNotificar);
        this.sessaoStore.atualizarListaProcessos();
    };

    gravarMovimentosOuDecisao(registro, movimentos, decisao) {
        return registro.processo.origem === Configuracao.ORIGEM_PJE
            ? this.gravarMovimentos(registro, movimentos)
            : this.gravarDecisao(registro, decisao);
    }

    getDecisaoSelecionadaParaGravar() {
        let decisao;

        if (this.existeProcessoSIJSelecionado) {
            decisao = this.dominioStore.decisoesProcessuais.find(
                (decisaoProcessual) => decisaoProcessual.id + '' === this.idDecisaoSelecionada
            );
        }
        return decisao;
    }

    getMovimentosSelecionadosParaGravar() {
        let movimentos;

        if (this.existeProcessoPJeSelecionado) {
            let ordem = 1;
            movimentos = this.movimentosSelecionados.map((movimento) => ({
                numeroMovimentoCnj: movimento.numeroMovimentoCnj,
                numeroOrdemMovimento: ordem++,
                complementos: this.converterComplementosParaGravar(movimento)
            }));
        }
        return movimentos;
    }

    gravarDecisao = async (registro, decisao) => {
        const resultadoProcessamento = await this.processoService.gravarDecisaoLote(registro, decisao.id);
        this.store.incrementarProgressoLote();

        if (resultadoProcessamento && !ErrorUtil.isResultadoErro(resultadoProcessamento)) {
            runInAction(() => (registro.informacaoProcessoSalaSessao.decisao = decisao));
        }

        return resultadoProcessamento;
    };

    gravarMovimentos = async (registro, movimentos) => {
        const resultadoProcessamento = await this.processoService.gravarMovimentosLote(registro, movimentos);
        this.store.incrementarProgressoLote();

        if (resultadoProcessamento && !ErrorUtil.isResultadoErro(resultadoProcessamento)) {
            runInAction(() => (registro.movimentos = movimentos));
        }

        return resultadoProcessamento;
    };

    getAlertasProcesso = (registro) => {
        const alertas = [];

        if (registro.necessitaImportacaoSalaSessao) {
            alertas.push({ mensagem: 'Processo não importado para sala de sessão', impeditivo: true });
        }

        if (registro.movimentosTransferidos) {
            alertas.push({ mensagem: 'Os movimentos já foram enviados ao PJe', impeditivo: true });
        }

        return alertas;
    };

    getTooltip() {
        return 'Registrar ' + this.label + ' em lote';
    }

    get label() {
        return this.labelMovimento && this.labelDecisao
            ? this.labelMovimento + '/' + this.labelDecisao
            : this.labelMovimento || this.labelDecisao;
    }

    get labelMovimento() {
        return this.store.existeSessaoPJe ? 'movimentos' : undefined;
    }

    get labelDecisao() {
        return this.store.existeSessaoSIJ ? 'decisões' : undefined;
    }

    callbackDecisaoSelecionada = (idDecisao) => {
        this.idDecisaoSelecionada = idDecisao;
    };

    callbackMovimentosSelecionados = (movimentos) => {
        this.movimentosSelecionados = movimentos;
    };

    converterComplementosParaGravar(movimento) {
        const retorno = [];
        let numeroOrdem = 1;

        if (movimento.complementos && movimento.complementos.length > 0) {
            for (const complemento of movimento.complementos || []) {
                if (this.isComplementoPreenchido(complemento)) {
                    const complementoGravar = {
                        codigoComplementoCnj: complemento.codigoComplementoCnj,
                        numeroOrdem: numeroOrdem++
                    };

                    if (complemento.codigoDominioComplementoCnj) {
                        complementoGravar['codigoDominioComplementoCnj'] = complemento.codigoDominioComplementoCnj;
                    } else {
                        complementoGravar['valor'] = complemento.valor;
                    }

                    retorno.push(complementoGravar);
                }
            }
        }
        return retorno;
    }

    isComplementoPreenchido(complemento) {
        return (
            complemento && (complemento.valor !== undefined || complemento.codigoDominioComplementoCnj !== undefined)
        );
    }

    render() {
        if (!this.isComponenteHabilitado('MovimentoDecisaoLote')) {
            return null;
        }
        return (
            <>
                <div
                    style={{ marginRight: '10px' }}
                    className="corBotao"
                >
                    <Tooltip
                        title={this.getTooltip()}
                        placement="top-start"
                    >
                        <SwipeRightAlt
                            style={{ cursor: 'pointer', width: '24px', height: '24px' }}
                            onClick={this.handleClickIcone}
                        />
                    </Tooltip>
                </div>
                {!!this.dialogOpen && (
                    <Dialog
                        open
                        fullWidth
                        maxWidth="xl"
                        onClose={this.handleCloseDialog}
                        slotProps={{ paper: { sx: { height: '100%' } } }}
                    >
                        {this.renderDialogTitle}
                        {this.renderConteudoDialog}
                        {this.renderDialogActions()}
                    </Dialog>
                )}
            </>
        );
    }

    get renderDialogTitle() {
        return (
            <DialogTitle style={{ padding: '16px 24px 0px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            flexGrow: 1
                        }}
                    >
                        <h4 style={{ margin: '0px' }}>Registrar {this.label} em lote</h4>
                        <div style={{ margin: '0px 10px' }}>
                            <NumeroProcesso
                                dense
                                tooltip="Número do processo a ser pesquisado na listagem"
                                callbackOnChange={this.callbackNumeroProcesso}
                                clickLimpar={this.limparProcessoPesquisa}
                            />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', margin: '10px', paddingTop: '5px' }}>
                                <PainelFiltros />
                            </div>
                            <FiltroRapido id="filtroRapidoMovimentoDecisaoLote" />
                        </div>
                    </div>
                </div>
            </DialogTitle>
        );
    }

    get renderConteudoDialog() {
        return (
            <>
                <OperacoesEmLoteSelecionar
                    processosSelecionaveis={this.processosSelecionaveis}
                    processos={this.processos}
                />
                <PainelProcessosOperacoesEmLote
                    processos={this.processos}
                    mensagemLoteSemProcessos={this.mensagemLoteSemProcessos}
                    alertasProcessos={this.alertasProcessos}
                />
            </>
        );
    }

    renderDialogActions() {
        return (
            <DialogActions style={{ height: '60px' }}>
                <div style={{ display: 'flex', flexGrow: 1 }}>
                    {!!this.existeProcessoPJeSelecionado && (
                        <div
                            style={{
                                display: 'flex',
                                width: this.existeProcessoSIJSelecionado ? '50%' : '100%',
                                margin: '0px 20px 0px 8px'
                            }}
                        >
                            <MovimentoCNJ
                                movimentos={this.movimentosSelecionados}
                                callbackMovimentosSelecionados={this.callbackMovimentosSelecionados}
                            />
                        </div>
                    )}
                    {!!this.existeProcessoSIJSelecionado && (
                        <div
                            style={{
                                display: 'flex',
                                width: this.existeProcessoPJeSelecionado ? '50%' : '100%',
                                margin: '0px 20px 0px 8px'
                            }}
                        >
                            <CodigoDecisao
                                id="movimentoDecisaoLote"
                                idDecisao={this.idDecisaoSelecionada}
                                callbackDecisaoSelecionada={this.callbackDecisaoSelecionada}
                            />
                        </div>
                    )}
                </div>
                <div style={{ display: 'flex' }}>
                    <Button
                        style={{ marginRight: '8px' }}
                        onClick={this.handleCloseDialog}
                        color="secondary"
                        variant="outlined"
                        endIcon={<CancelIcon />}
                    >
                        Fechar
                    </Button>
                    <Tooltip title={this.tooltipBotaoRegistrar}>
                        <div>
                            <Button
                                style={{ marginRight: '8px' }}
                                onClick={this.handleClickBotaoRegistrar}
                                color="primary"
                                variant="outlined"
                                endIcon={<SaveIcon />}
                                disabled={this.botaoRegistrarDesabilitado}
                            >
                                Registrar
                            </Button>
                        </div>
                    </Tooltip>
                </div>
            </DialogActions>
        );
    }

    get botaoRegistrarDesabilitado() {
        return (
            !this.processosSelecionados ||
            this.processosSelecionados.length === 0 ||
            this.tooltipBotaoRegistrar !== null
        );
    }

    get tooltipBotaoRegistrar() {
        const mensagens = [];

        if (
            this.existeProcessoPJeSelecionado &&
            (!this.movimentosSelecionados || this.movimentosSelecionados.length === 0)
        ) {
            mensagens.push('Informe o Movimento CNJ para registrar nos processos PJe selecionados');
        }

        if (this.existeProcessoSIJSelecionado && !this.idDecisaoSelecionada) {
            mensagens.push('Informe o Código de Decisão para registrar nos processos SIJ selecionados');
        }

        if (mensagens.length === 0) {
            return null;
        }

        return (
            <div>
                {mensagens.map((msg) => (
                    <div key={this.nextKey}>{msg}</div>
                ))}
            </div>
        );
    }
}

export default inject('stores')(observer(MovimentoDecisaoLote));
